import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Product, productService } from '../../services/productService';
import saleService from '../../services/saleService';
import CustomCombobox from '../common/Combobox';
import toast from 'react-hot-toast';
import { userService } from '../../services/userService';
import { Edit, Package, AlertTriangle, Info, UserCircle, RotateCcw, ShoppingCart } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { UserRole, User } from '../../types';
import { ProductSelectorModal } from '../ProductSelectorModal';

interface CentralEditModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;

    // Product being edited (from inventory or pre-orders)
    product?: Product;

    // Or if opened from SaleDetailModal:
    saleId?: number;
    saleItem?: any; // Contains product and sale details
}

export const CentralEditModal: React.FC<CentralEditModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    product,
    saleId,
    saleItem
}) => {
    const { user } = useAuth();
    const isAdmin = user?.rol_usr === UserRole.ADMIN;

    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<'attributes' | 'sale'>(saleId ? 'sale' : 'attributes');

    // -- Unified Editing State --
    const [infoForm, setInfoForm] = useState({
        cod_producto: '',
        marca: '',
        color: '',
        talla: '',
        precio: 0
    });
    const [originalPrice, setOriginalPrice] = useState(0);
    const [originalInvType, setOriginalInvType] = useState<'maestro' | 'novias'>('maestro');
    const [inventoryType, setInventoryType] = useState<'maestro' | 'novias'>('maestro');
    const [priceEditReason, setPriceEditReason] = useState<string>('');

    // -- Autocomplete data --
    const [existingBrands, setExistingBrands] = useState<string[]>([]);
    const [existingCodes, setExistingCodes] = useState<string[]>([]);
    const [codeBrands, setCodeBrands] = useState<{ code: string, brand: string }[]>([]);
    const [filterData, setFilterData] = useState<any>(null);

    // -- Swap Flow State --
    const [showProductSelector, setShowProductSelector] = useState(false);
    
    // -- Create New Pre-Order Flow State --
    const [showCreatePreOrder, setShowCreatePreOrder] = useState(false);
    const [newPreOrderData, setNewPreOrderData] = useState({
        cod_producto: '',
        marca: '',
        color: '',
        talla: 'UNI',
        precio: 0,
        inventoryType: 'maestro' as 'maestro' | 'novias'
    });

    // -- Seller Reassignment State --
    const [sellers, setSellers] = useState<User[]>([]);
    const [selectedSellerId, setSelectedSellerId] = useState<string>('');

    // --- On Mount ---
    useEffect(() => {
        if (isOpen) {
            const activeProduct = product || saleItem?.product;

            if (activeProduct) {
                setInfoForm({
                    cod_producto: activeProduct.cod_producto || '',
                    marca: activeProduct.marca || '',
                    color: activeProduct.color || '',
                    talla: activeProduct.talla || '',
                    precio: saleItem?.unitPrice ?? activeProduct.precio ?? 0
                });

                setOriginalPrice(saleItem?.unitPrice ?? activeProduct.precio ?? 0);

                const currentInvType = activeProduct.inventoryType || saleItem?.inventoryType || 'maestro';
                setInventoryType(currentInvType);
                setOriginalInvType(currentInvType);

                // Pre-fill create pre-order form
                setNewPreOrderData({
                    cod_producto: activeProduct.cod_producto || '',
                    marca: activeProduct.marca || '',
                    color: activeProduct.color || '',
                    talla: activeProduct.talla || 'UNI',
                    precio: saleItem?.unitPrice ?? activeProduct.precio ?? 0,
                    inventoryType: currentInvType
                });
            }

            loadOptions();

            if (isAdmin && saleId) {
                userService.getAllUsers().then(users => {
                    const activeSellers = users.filter((u: any) => u.estado !== false && (u.role || u.rol_usr) !== 'CLIENTE');
                    setSellers(activeSellers);
                    if (saleItem?.sales?.sellerId) {
                        setSelectedSellerId(saleItem.sales.sellerId.toString());
                    }
                }).catch(err => console.error("Error cargando vendedoras", err));
            }
        }
    }, [isOpen, product, saleItem, saleId, isAdmin]);

    const loadOptions = async () => {
        try {
            const response = await productService.getFilterOptions();
            if (response) {
                const filterData = response as any;
                const maestroBrands = filterData.maestro?.marcas || [];
                const noviasBrands = filterData.novias?.marcas || [];

                setFilterData(filterData);
                setExistingBrands(Array.from(new Set([...maestroBrands, ...noviasBrands])).sort() as string[]);

                const maestroCodes = filterData.maestro?.codeBrands || [];
                const noviasCodes = filterData.novias?.codeBrands || [];
                setCodeBrands([...maestroCodes, ...noviasCodes]);

                setExistingCodes(
                    Array.from(new Set([...maestroCodes.map((cb: any) => cb.code), ...noviasCodes.map((cb: any) => cb.code)])) as string[]
                );
            }
        } catch (e) {
            console.error("Error loading options", e);
        }
    };

    const getColorsForBrand = (brand: string): string[] => {
        if (!brand || !filterData) return [];
        const invData = inventoryType === 'maestro' ? filterData.maestro : filterData.novias;
        if (!invData?.brandColors) return [];
        return Array.from(new Set(invData.brandColors.filter((bc: any) => bc.brand === brand).map((bc: any) => bc.color))) as string[];
    };

    const getSizesForBrand = (brand: string): string[] => {
        if (!brand || !filterData) return [];
        const invData = inventoryType === 'maestro' ? filterData.maestro : filterData.novias;
        if (!invData?.brandSizes) return [];
        return Array.from(new Set(invData.brandSizes.filter((bs: any) => bs.brand === brand).map((bs: any) => bs.size))) as string[];
    };

    // --- ACTIONS ---

    const handleSaveUnified = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isAdmin) return;

        if (!infoForm.marca || !infoForm.cod_producto) {
            toast.error('Marca y Código son obligatorios');
            return;
        }

        const priceChanged = infoForm.precio !== originalPrice;
        if (saleId && priceChanged && !priceEditReason.trim()) {
            toast.error('Debes proporcionar un motivo para el cambio de precio');
            return;
        }

        if (!window.confirm('¿Estás seguro de aplicar estos cambios?')) {
            return;
        }

        setLoading(true);
        const activeProduct = product || saleItem?.product;

        try {
            // 1. Modificar atributos físicos
            const isPreOrder = activeProduct.pre_order || saleItem?.isPreOrder;
            if (isPreOrder) {
                await productService.correctPreOrder(activeProduct.id_producto, {
                    ...infoForm,
                    type: inventoryType
                });
            } else {
                await productService.updateProduct(activeProduct.id_producto, {
                    ...infoForm,
                    type: inventoryType
                });
                
                // Switch inventory type if changed explicitly for non-preorder
                if (inventoryType !== originalInvType) {
                    if (saleId && saleItem) {
                        await saleService.updateSaleItemInventoryType(saleId, saleItem.id, inventoryType);
                    } else {
                        await productService.switchInventoryType(activeProduct.id_producto, inventoryType);
                    }
                }
            }

            // 2. Modificar Precio en la Nota de Venta si es necesario
            if (saleId && priceChanged) {
                await saleService.updateSaleItemPrice(
                    saleId,
                    saleItem.id,
                    infoForm.precio,
                    priceEditReason
                );
            }

            toast.success('Atributos y configuraciones actualizados exitosamente');
            onSuccess();
            onClose();
        } catch (error: any) {
            console.error(error);
            toast.error(error.response?.data?.message || 'Error al actualizar producto');
        } finally {
            setLoading(false);
        }
    };

    const handleSwapProduct = async (selectedProduct: Product) => {
        if (!saleId || !saleItem) return;

        try {
            setLoading(true);
            setShowProductSelector(false);

            await saleService.swapSaleItem(
                saleId,
                saleItem.id,
                selectedProduct.id_producto,
                selectedProduct.inventoryType || 'maestro'
            );

            toast.success('Artículo reemplazado exitosamente en la venta');
            onSuccess();
            onClose();
        } catch (error: any) {
            console.error(error);
            toast.error(error.response?.data?.message || 'Error al reemplazar artículo');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateSwapPreOrder = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!saleId || !saleItem) return;

        if (!newPreOrderData.cod_producto.trim() || !newPreOrderData.marca.trim()) {
            toast.error('Código y Marca son obligatorios para el nuevo Pre-order');
            return;
        }

        if (!window.confirm('¿Confirmas crear este nuevo registro especial de Pre-order y asignarlo a esta venta?')) {
            return;
        }

        try {
            setLoading(true);

            // Buscar si ya existe en inventario regular con stock
            try {
                const searchResponse = await productService.getProducts({
                    search: newPreOrderData.cod_producto.toUpperCase(),
                    exactMatch: true,
                    type: newPreOrderData.inventoryType
                });

                const exactMatch = searchResponse.data.find(p => 
                    p.cod_producto?.toUpperCase() === newPreOrderData.cod_producto.toUpperCase() &&
                    p.color?.toUpperCase() === newPreOrderData.color.toUpperCase() &&
                    p.talla?.toUpperCase() === newPreOrderData.talla.toUpperCase() &&
                    p.stock && p.stock > 0 &&
                    p.activo === true &&
                    p.pre_order === false
                );

                if (exactMatch) {
                    setLoading(false);
                    const proceed = window.confirm(`¡ATENCIÓN!\n\nYa existe una prenda en inventario regular (Stock: ${exactMatch.stock}) con el código ${exactMatch.cod_producto}, color ${exactMatch.color} y talla ${exactMatch.talla}.\n\n¿Estás SEGURO que quieres generar un NUEVO Pre-order especial en lugar de usar la opción "Cambiar por prenda de inventario"?`);
                    if (!proceed) return;
                    setLoading(true);
                }
            } catch (err) {
                console.error("Error al buscar coincidencias en inventario:", err);
                // Si falla la búsqueda de advertencia, continuamos con el flujo normal
            }
            
            // 1. Create Pre-order Product
            const newProduct = await productService.createProduct({
                cod_producto: newPreOrderData.cod_producto.toUpperCase(),
                marca: newPreOrderData.marca,
                color: newPreOrderData.color || 'POR DEFINIR',
                talla: newPreOrderData.talla || 'UNI',
                precio: newPreOrderData.precio,
                stock: 1,
                activo: true,
                pre_order: true,
                type: newPreOrderData.inventoryType,
                generate_qr: true
            });

            // 2. Swap sale item
            await saleService.swapSaleItem(
                saleId,
                saleItem.id,
                newProduct.id_producto,
                newPreOrderData.inventoryType
            );

            toast.success('Pre-order creado e intercambiado exitosamente');
            onSuccess();
            onClose();
        } catch (error: any) {
            console.error(error);
            toast.error(error.response?.data?.message || 'Error al crear e intercambiar pre-order');
        } finally {
            setLoading(false);
        }
    };

    const handleReassignSeller = async () => {
        if (!saleId || !selectedSellerId) return;

        if (!window.confirm('¿Seguro que deseas reasignar las comisiones de esta venta a la nueva vendedora?')) {
            return;
        }

        try {
            setLoading(true);
            const res = await saleService.updateSaleSeller(saleId, selectedSellerId);
            toast.success(res.message || 'Vendedora y comisiones actualizadas');
            onSuccess();
        } catch (error: any) {
            console.error(error);
            toast.error(error.response?.data?.message || 'Error reasignando vendedora');
        } finally {
            setLoading(false);
        }
    };


    if (!isOpen) return null;

    const activeProduct = product || saleItem?.product;
    const isCurrentPreOrder = activeProduct?.pre_order || saleItem?.isPreOrder;

    return createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[1000] overflow-y-auto" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-xl flex flex-col animate-fadeIn my-auto" onClick={(e) => e.stopPropagation()}>

                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center sticky top-0 z-10 rounded-t-2xl">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                            <Edit size={20} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 tracking-tight">
                                Centro de Edición
                            </h2>
                            <p className="text-sm text-gray-500 mt-0.5">
                                {saleId ? `Editando artículo de Venta #${saleId}` : `Editando producto: ${activeProduct?.cod_producto}`}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                        <XMarkIcon className="w-6 h-6 text-gray-500" />
                    </button>
                </div>

                {!isAdmin && (
                    <div className="bg-amber-50 p-3 text-amber-800 text-sm flex items-center justify-center gap-2 border-b border-amber-200">
                        <Info size={16} /> Solo modo lectura. Se requieren permisos de Administrador para guardar cambios.
                    </div>
                )}

                {/* Tabs */}
                <div className="flex border-b border-gray-200 px-6 pt-2 bg-white sticky top-16 z-10">
                    <button
                        onClick={() => setActiveTab('attributes')}
                        className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'attributes' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    >
                        <Edit size={16} /> Atributos Físicos
                    </button>
                    {saleId && (
                        <button
                            onClick={() => setActiveTab('sale')}
                            className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'sale' ? 'border-orange-600 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        >
                            <ShoppingCart size={16} /> Ajustes en Venta
                        </button>
                    )}
                </div>

                {/* Content Area */}
                <div className="p-6 overflow-y-auto max-h-[75vh] bg-white space-y-10 custom-scrollbar">

                    {/* TAB 1: Editar Atributos Físicos */}
                    {activeTab === 'attributes' && (
                        <section className="animate-fadeIn">
                            <h3 className="text-lg font-bold text-gray-900 border-b pb-2 mb-4 flex items-center gap-2">
                                <Edit size={18} className="text-blue-600" /> Corrección de Catálogo
                            </h3>
                            <p className="text-sm text-gray-600 mb-4 bg-blue-50 p-3 rounded-lg border border-blue-100">
                                Estos cambios afectarán a la prenda en el inventario global.
                            </p>
                            
                            <form onSubmit={handleSaveUnified} className="grid grid-cols-2 gap-4 bg-gray-50/50 p-5 rounded-xl border border-gray-100">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Marca</label>
                                <CustomCombobox
                                    value={infoForm.marca}
                                    onChange={(val) => isAdmin && setInfoForm({ ...infoForm, marca: val, color: '', talla: '' })}
                                    options={existingBrands}
                                    placeholder="Selecciona Marca"
                                    className="w-full"
                                    allowCustom={false}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Código</label>
                                <CustomCombobox
                                    value={infoForm.cod_producto}
                                    onChange={(val) => {
                                        if (!isAdmin) return;
                                        const found = codeBrands.find(cb => cb.code.toLowerCase() === val.toLowerCase());
                                        setInfoForm({ ...infoForm, cod_producto: val, marca: found ? found.brand : infoForm.marca });
                                    }}
                                    options={existingCodes}
                                    placeholder="Código..."
                                    className="w-full"
                                    allowCustom={true}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Color</label>
                                <CustomCombobox
                                    value={infoForm.color}
                                    onChange={(val) => isAdmin && setInfoForm({ ...infoForm, color: val })}
                                    options={getColorsForBrand(infoForm.marca)}
                                    placeholder="Color..."
                                    className="w-full"
                                    allowCustom={true}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Talla</label>
                                <CustomCombobox
                                    value={infoForm.talla}
                                    onChange={(val) => isAdmin && setInfoForm({ ...infoForm, talla: val })}
                                    options={getSizesForBrand(infoForm.marca)}
                                    placeholder="Talla..."
                                    className="w-full"
                                    allowCustom={true}
                                />
                            </div>

                            <div className="col-span-2 sm:col-span-1">
                                <label className="block text-sm font-bold text-gray-700 mb-1">Inventario</label>
                                <select
                                    value={inventoryType}
                                    onChange={(e) => setInventoryType(e.target.value as any)}
                                    disabled={!isAdmin}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 h-[38px]"
                                >
                                    <option value="maestro">Inventario Maestro</option>
                                    <option value="novias">Inventario Novias</option>
                                </select>
                            </div>

                            <div className="col-span-2 sm:col-span-1">
                                <label className="block text-sm font-bold text-gray-700 mb-1">Precio Unitario ($)</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={infoForm.precio}
                                    onChange={(e) => isAdmin && setInfoForm({ ...infoForm, precio: parseFloat(e.target.value) || 0 })}
                                    disabled={!isAdmin}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm font-bold text-blue-700 h-[38px]"
                                />
                            </div>

                            {saleId && infoForm.precio !== originalPrice && (
                                <div className="col-span-2 animate-fadeIn bg-orange-50 border border-orange-200 p-3 rounded-lg mt-2">
                                    <label className="block text-xs font-bold text-orange-800 mb-1">Motivo del Cambio de Precio *</label>
                                    <input
                                        type="text"
                                        value={priceEditReason}
                                        onChange={(e) => setPriceEditReason(e.target.value)}
                                        disabled={!isAdmin}
                                        className="w-full px-2 py-1.5 border border-orange-200 rounded text-sm focus:ring-1 focus:ring-orange-500"
                                        placeholder="Ej: Autorizado por gerencia..."
                                    />
                                </div>
                            )}

                            <div className="col-span-2 mt-2">
                                <button type="submit" disabled={loading || !isAdmin} className="w-full py-2.5 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm">
                                    {loading ? 'Guardando...' : 'Guardar Atributos'}
                                </button>
                            </div>
                        </form>
                    </section>
                    )}

                    {/* TAB 2: Ajustes de Venta */}
                    {activeTab === 'sale' && saleId && (
                        <div className="space-y-10 animate-fadeIn">
                            

                    {saleId && (
                        <section>
                            <h3 className="text-lg font-bold text-gray-900 border-b pb-2 mb-4 flex items-center gap-2">
                                <RotateCcw size={18} className="text-orange-600" /> Intercambiar Producto
                            </h3>
                            
                            {!showCreatePreOrder ? (
                                <div className="space-y-4 bg-orange-50/50 p-5 rounded-xl border border-orange-100">
                                    <p className="text-sm text-gray-600 mb-2">
                                        Reemplaza la prenda actual en la nota de venta. El sistema ajustará automáticamente el inventario según el origen y destino.
                                    </p>
                                    
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <button
                                            onClick={() => setShowProductSelector(true)}
                                            disabled={!isAdmin}
                                            className="w-full py-3 border-2 border-orange-200 text-orange-700 bg-white rounded-xl font-bold hover:bg-orange-100 transition-colors flex flex-col items-center justify-center gap-1 shadow-sm"
                                        >
                                            <Package size={20} />
                                            <span>Cambiar por Stock</span>
                                        </button>
                                        
                                        <button
                                            onClick={() => setShowCreatePreOrder(true)}
                                            disabled={!isAdmin}
                                            className="w-full py-3 border-2 border-orange-200 text-orange-700 bg-white rounded-xl font-bold hover:bg-orange-100 transition-colors flex flex-col items-center justify-center gap-1 shadow-sm"
                                        >
                                            <AlertTriangle size={20} />
                                            <span>Cambiar por Pre-Order</span>
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-orange-50 p-5 border-2 border-orange-200 rounded-2xl animate-fadeIn">
                                    <div className="flex justify-between items-start mb-4">
                                        <h4 className="font-bold text-orange-800 flex items-center gap-2">
                                            <AlertTriangle size={16} /> Crear y Asignar Pre-Order
                                        </h4>
                                        <button onClick={() => setShowCreatePreOrder(false)} className="text-orange-400 hover:text-orange-600">
                                            <XMarkIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                    <p className="text-xs text-orange-700 mb-4 bg-white p-2 rounded border border-orange-100">
                                        Ingresa los datos de la nueva prenda especial. Se creará en el sistema y reemplazará al artículo actual ({isCurrentPreOrder ? 'Pre-order' : 'Stock'}) en la nota de venta.
                                    </p>

                                    <form onSubmit={handleCreateSwapPreOrder} className="grid grid-cols-2 gap-3">
                                        <div className="col-span-2 sm:col-span-1">
                                            <label className="block text-xs font-black text-gray-500 uppercase mb-1">Marca *</label>
                                            <CustomCombobox
                                                value={newPreOrderData.marca}
                                                onChange={(val) => setNewPreOrderData({...newPreOrderData, marca: val})}
                                                options={existingBrands}
                                                placeholder="Marca"
                                                className="w-full"
                                                allowCustom={true}
                                            />
                                        </div>
                                        <div className="col-span-2 sm:col-span-1">
                                            <label className="block text-xs font-black text-gray-500 uppercase mb-1">Código *</label>
                                            <input
                                                type="text"
                                                value={newPreOrderData.cod_producto}
                                                onChange={(e) => setNewPreOrderData({...newPreOrderData, cod_producto: e.target.value})}
                                                className="w-full px-3 py-1.5 border border-orange-200 rounded text-sm focus:ring-1 focus:ring-orange-500"
                                                placeholder="Código"
                                            />
                                        </div>
                                        <div className="col-span-2 sm:col-span-1">
                                            <label className="block text-xs font-black text-gray-500 uppercase mb-1">Color</label>
                                            <CustomCombobox
                                                value={newPreOrderData.color}
                                                onChange={(val) => setNewPreOrderData({...newPreOrderData, color: val})}
                                                options={getColorsForBrand(newPreOrderData.marca)}
                                                placeholder="Color"
                                                className="w-full"
                                                allowCustom={true}
                                            />
                                        </div>
                                        <div className="col-span-2 sm:col-span-1">
                                            <label className="block text-xs font-black text-gray-500 uppercase mb-1">Talla</label>
                                            <CustomCombobox
                                                value={newPreOrderData.talla}
                                                onChange={(val) => setNewPreOrderData({...newPreOrderData, talla: val})}
                                                options={getSizesForBrand(newPreOrderData.marca)}
                                                placeholder="Talla"
                                                className="w-full"
                                                allowCustom={true}
                                            />
                                        </div>
                                        <div className="col-span-2 sm:col-span-1">
                                            <label className="block text-xs font-black text-gray-500 uppercase mb-1">Precio Unitario</label>
                                            <input
                                                type="number"
                                                value={newPreOrderData.precio}
                                                onChange={(e) => setNewPreOrderData({...newPreOrderData, precio: parseFloat(e.target.value) || 0})}
                                                className="w-full px-3 py-1.5 border border-orange-200 rounded text-sm font-bold focus:ring-1 focus:ring-orange-500 h-[38px]"
                                            />
                                        </div>
                                        <div className="col-span-2 sm:col-span-1">
                                            <label className="block text-xs font-black text-gray-500 uppercase mb-1">Inventario Destino</label>
                                            <select
                                                value={newPreOrderData.inventoryType}
                                                onChange={(e) => setNewPreOrderData({...newPreOrderData, inventoryType: e.target.value as any})}
                                                className="w-full px-3 py-1.5 border border-orange-200 rounded text-sm bg-white h-[38px]"
                                            >
                                                <option value="maestro">Maestro</option>
                                                <option value="novias">Novias</option>
                                            </select>
                                        </div>
                                        <div className="col-span-2 mt-2">
                                            <button type="submit" disabled={loading} className="w-full py-2.5 bg-orange-600 text-white rounded-lg font-bold hover:bg-orange-700 disabled:opacity-50 transition-colors shadow-md">
                                                {loading ? 'Creando...' : 'Crear e Intercambiar'}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            )}
                        </section>
                    )}

                    {/* SECTION 3: Reasignar Vendedora (Only in sale context) */}
                    {saleId && (
                        <section className="pt-4 border-t border-gray-100">
                            <h3 className="text-lg font-bold text-gray-900 border-b pb-2 mb-4 flex items-center gap-2">
                                <UserCircle size={18} className="text-purple-600" /> Reasignar Vendedora
                            </h3>
                            <div className="bg-purple-50 p-5 border border-purple-100 rounded-xl space-y-4">
                                <p className="text-sm text-purple-800 font-medium">
                                    Cambiar la vendedora reasignará la comisión de esta venta a la nueva persona.
                                </p>
                                <div className="flex flex-col sm:flex-row gap-3 items-end">
                                    <div className="flex-1 w-full">
                                        <label className="block text-sm font-bold text-gray-700 mb-1">Personal de Ventas</label>
                                        <select
                                            value={selectedSellerId}
                                            onChange={(e) => setSelectedSellerId(e.target.value)}
                                            disabled={!isAdmin || loading}
                                            className="w-full px-3 py-2.5 border border-purple-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500 font-semibold text-gray-800"
                                        >
                                            <option value="" disabled>Selecciona vendedora...</option>
                                            {sellers.map((s) => (
                                                <option key={s.id_usuario?.toString()} value={s.id_usuario?.toString()}>
                                                    {s.username} - {(s as any).role || s.rol_usr}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <button
                                        onClick={handleReassignSeller}
                                        disabled={loading || !isAdmin || !selectedSellerId || (saleItem?.sales?.sellerId?.toString() === selectedSellerId)}
                                        className="w-full sm:w-auto px-6 py-2.5 bg-purple-600 text-white rounded-lg font-bold hover:bg-purple-700 disabled:opacity-50 transition-colors shadow-sm"
                                    >
                                        {loading ? 'Aplicando...' : 'Reasignar'}
                                    </button>
                                </div>
                            </div>
                        </section>
                    )}
                        </div>
                    )}
                </div>
            </div>

            {/* Product Selector Modal for "Cambiar por Stock" */}
            {showProductSelector && (
                <ProductSelectorModal
                    isOpen={showProductSelector}
                    onClose={() => setShowProductSelector(false)}
                    onSelect={handleSwapProduct}

                />
            )}
        </div>,
        document.body
    );
};
